// Save All (spec/dashboard.md section 4.3): the pre-save hooks, one `documents:batch` with atomic: false, per-entity
// results mapped back onto the drafts, version conflicts detected, nothing staged ever lost.
import { ApiError, type ApiClient } from '../api/client';
import { ops } from '../api/operations';
import type { BatchOperationRequest, Document, Json } from '../api/types';
import { getAt } from './paths';
import { isTempId, type Draft, type EntityKey, type SaveSummary, type Workspace } from './Workspace';

export interface PreSaveBlock {
  key: EntityKey;
  extension: string;
  reason: string;
  /** a warning is shown with the save result and never blocks (section 10.6) */
  warning?: boolean;
}

export interface PreSaveContext {
  drafts: Draft[];
  workspace: Workspace;
}

/** Slice 6 fills these with the extensions' preSave; slice 1 ships the hook point. Each hook names the entities it blocks. */
export type PreSaveHook = (ctx: PreSaveContext) => Promise<PreSaveBlock[]>;

export interface SaveContext {
  client: ApiClient;
  org: string;
  game: string;
  branch: string;
  hooks?: PreSaveHook[];
  /** the commit message (the user's or the automatic summary, ./summary.ts) */
  message?: string;
  /** the reason every player-key write carries (section 11.4); when absent the message stands in for it */
  reason?: string;
  dryRun?: boolean;
}

function reasonOf(ctx: SaveContext): string {
  return (ctx.reason ?? ctx.message ?? '').trim();
}

/** A document draft's `current` is the DocumentDto shape (data, export, tags, template, …); field paths start with `data.`. */
export type DocumentDraftValue = Pick<Document, 'template' | 'export' | 'tags' | 'data'> & Partial<Document>;

function refTempIds(value: Json, tempIds: Set<string>): Json {
  if (typeof value === 'string') return tempIds.has(value) ? ({ $ref: value } as unknown as Json) : value;
  if (Array.isArray(value)) return value.map((v) => refTempIds(v, tempIds));
  if (value !== null && typeof value === 'object') {
    const out: Record<string, Json> = {};
    for (const [k, v] of Object.entries(value)) out[k] = refTempIds(v, tempIds);
    return out;
  }
  return value;
}

const emptyOperation: BatchOperationRequest = { op: null, tempId: null, id: null, template: null, ifVersion: null, data: null, export: null, tags: null, unnyId: null, meta: null };

/** The batch operation of one document draft. */
export function operationOf(draft: Draft, tempIds: Set<string>): BatchOperationRequest {
  const value = draft.current as unknown as DocumentDraftValue;
  if (draft.status === 'deleted') {
    return { ...emptyOperation, op: 'delete', id: draft.id, ifVersion: draft.base?.version ?? null };
  }
  if (draft.status === 'new') {
    return {
      ...emptyOperation,
      op: 'create',
      tempId: draft.id,
      template: value.template,
      data: refTempIds(value.data as Json, tempIds),
      export: value.export ?? true,
      tags: value.tags ?? [],
    };
  }
  // modified: the changed top-level fields of data, whole (PATCH merges top-level keys), plus export / tags when they moved
  const data: Record<string, Json> = {};
  let exportChanged = false;
  let tagsChanged = false;
  for (const change of draft.changes) {
    if (change.path.startsWith('data.') || change.path.startsWith('data[')) {
      const top = change.path.slice(5).split(/[.[]/)[0]!;
      data[top] = refTempIds(getAt(value, `data.${top}`) as Json, tempIds);
    } else if (change.path === 'export') exportChanged = true;
    else if (change.path === 'tags' || change.path.startsWith('tags[')) tagsChanged = true;
  }
  const op: BatchOperationRequest = { ...emptyOperation, op: 'update', id: draft.id, ifVersion: draft.base?.version ?? null };
  if (Object.keys(data).length > 0) op.data = data as unknown as BatchOperationRequest['data'];
  if (exportChanged) op.export = value.export;
  if (tagsChanged) op.tags = value.tags;
  return op;
}

/** A localization-string draft's `current` is the LocalizationStringDto shape; new and modified drafts upsert, deleted ones delete. */
export type StringDraftValue = { group: string; key: string; description?: string | null; export?: boolean; values?: Record<string, string> };

export function stringOperationOf(draft: Draft) {
  const v = draft.current as unknown as StringDraftValue;
  if (draft.status === 'deleted') return { op: 'delete', group: v.group, key: v.key, description: null, export: null, values: null };
  return { op: 'upsert', group: v.group, key: v.key, description: v.description ?? null, export: v.export ?? true, values: v.values ?? {} };
}

export async function saveAll(workspace: Workspace, ctx: SaveContext): Promise<SaveSummary> {
  const dryRun = ctx.dryRun ?? false;
  workspace.clearOutcomes();
  const drafts = workspace.all;
  const summary: SaveSummary = { at: Date.now(), saved: 0, failed: 0, blocked: 0, dryRun, commit: null };

  // 1. the pre-save hooks (every hook runs; blocks are per entity)
  const blocked = new Map<EntityKey, PreSaveBlock[]>();
  for (const hook of ctx.hooks ?? []) {
    let blocks: PreSaveBlock[] = [];
    try {
      blocks = await hook({ drafts, workspace });
    } catch (e) {
      blocks = []; // a throwing hook allows (fail-open, surfaced by the extension host — slice 6)
      void e;
    }
    for (const b of blocks) {
      if (b.warning) {
        workspace.markWarnings(b.key, [`${b.extension}: ${b.reason}`]);
        continue;
      }
      const list = blocked.get(b.key) ?? [];
      list.push(b);
      blocked.set(b.key, list);
    }
  }
  for (const [key, blocks] of blocked) {
    workspace.markFailed(key, { source: 'extension', message: blocks.map((b) => b.reason).join('\n'), reasons: blocks.map((b) => ({ extension: b.extension, reason: b.reason })) });
    summary.blocked++;
  }

  // 2. localization strings first (`strings:batch`): a new string's id must exist before a document references it (section 6.4)
  const stringDrafts = drafts.filter((d) => d.kind === 'string' && !blocked.has(d.key));
  // a new string needs a key and a value in the default language (section 9.1) — refused here with the reason, never sent
  const refused = new Set<EntityKey>();
  for (const d of stringDrafts) {
    if (d.status !== 'new') continue;
    const v = d.current as unknown as StringDraftValue & { defaultLanguage?: string };
    const key = (v.key ?? '').trim();
    const hasDefault = v.defaultLanguage ? (v.values?.[v.defaultLanguage] ?? '').trim().length > 0 : Object.values(v.values ?? {}).some((t) => (t ?? '').trim().length > 0);
    const reason = !key ? 'A key is required.' : !hasDefault ? `A value in the default language${v.defaultLanguage ? ` (${v.defaultLanguage})` : ''} is required.` : null;
    if (reason) {
      refused.add(d.key);
      summary.failed++;
      workspace.markFailed(d.key, { source: 'server', message: reason });
    }
  }
  // the batch addresses strings by GROUP/KEY (section 13.1), so a staged rename of an existing string goes through its PATCH instead
  const renamed = stringDrafts.filter((d) => {
    if (d.status !== 'modified' || !d.base) return false;
    const base = d.base.value as unknown as StringDraftValue;
    const cur = d.current as unknown as StringDraftValue;
    return base.group !== cur.group || base.key !== cur.key;
  });
  const strings = stringDrafts.filter((d) => !refused.has(d.key) && !renamed.includes(d));
  if (strings.length > 0) {
    const operations = strings.map(stringOperationOf);
    try {
      const result = await ctx.client.call(ops.batchLocalizationStrings, {
        path: { org: ctx.org, game: ctx.game, branch: ctx.branch },
        body: { dryRun, message: ctx.message?.trim() || `Save All: ${strings.length} localization string${strings.length === 1 ? '' : 's'}`, operations },
      });
      if (result.commit) workspace.setHead(result.commit);
      const rows = (result.data.results ?? []) as unknown as { row: number | string; id?: string | null; status: string; error?: string | null }[];
      for (const r of rows) {
        const draft = strings[Number(r.row)];
        if (!draft) continue;
        if (r.status === 'failed') {
          summary.failed++;
          workspace.markFailed(draft.key, { source: 'server', message: r.error ?? 'The server refused the string.' });
        } else {
          summary.saved++;
          if (!dryRun) workspace.markSaved(draft.key, { id: r.id ?? undefined });
        }
      }
    } catch (e) {
      // the strings batch writes nothing when any operation fails: the failing rows carry their detail, the rest wait for them
      const err = e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), '');
      const byIndex = new Map<number, string>();
      for (const d of err.details ?? []) if (typeof d.index === 'number') byIndex.set(d.index, d.message);
      strings.forEach((d, i) => {
        summary.failed++;
        workspace.markFailed(d.key, { source: err.status === 0 ? 'network' : 'server', message: byIndex.get(i) ?? (byIndex.size > 0 ? 'Not saved: another string of the batch failed (the batch writes nothing when any row fails).' : err.message), details: err.details ?? null });
      });
    }
  }

  for (const d of renamed) {
    if (dryRun) {
      summary.saved++;
      continue;
    }
    const v = d.current as unknown as StringDraftValue;
    try {
      const r = await ctx.client.data(ops.updateLocalizationString, {
        path: { org: ctx.org, game: ctx.game, branch: ctx.branch, string: d.id },
        body: { group: v.group, key: v.key, description: v.description ?? null, export: v.export ?? true, values: v.values ?? {}, message: ctx.message?.trim() || null } as never,
        ifMatch: d.base?.version ?? null,
      });
      summary.saved++;
      workspace.markSaved(d.key, { version: Number(r.version) });
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), '');
      summary.failed++;
      if (err.status === 412) workspace.markConflict(d.key, { remoteVersion: null, remoteValue: undefined, message: 'The string changed on the server since; reload and re-apply your change.' });
      else workspace.markFailed(d.key, { source: err.status === 0 ? 'network' : 'server', message: err.message, details: err.details ?? null });
    }
  }

  // 3. documents, one batch per actor group (Q-L-19: an extension's writes fail or land apart from the user's)
  const tempIds = new Set(drafts.filter((d) => d.kind === 'document' && isTempId(d.id)).map((d) => d.id));
  for (const actor of workspace.actors) {
    const docs = workspace.draftsOf(actor).filter((d) => d.kind === 'document' && !blocked.has(d.key));
    if (docs.length === 0) continue;
    const operations = docs.map((d) => operationOf(d, tempIds));
    const message = ctx.message?.trim() || `Save All: ${docs.length} document${docs.length === 1 ? '' : 's'}${actor === 'user' ? '' : ` by ${actor}`}`;
    try {
      const result = await ctx.client.call(ops.batchDocuments, {
        path: { org: ctx.org, game: ctx.game, branch: ctx.branch },
        body: { atomic: false, dryRun, message, operations },
      });
      const body = result.data;
      if (body.commitId) summary.commit = body.commitId;
      if (result.commit) workspace.setHead(result.commit);
      for (const r of body.results) {
        const draft = docs[Number(r.index)];
        if (!draft) continue;
        if (r.status === 'applied' || (dryRun && r.status === 'skipped' && !r.error)) {
          summary.saved++;
          if (!dryRun && isTempId(draft.id) && r.id) (summary.idMapping ??= {})[draft.id] = String(r.id);
          if (!dryRun) workspace.markSaved(draft.key, { id: r.id ?? undefined, version: r.version === null || r.version === undefined ? undefined : Number(r.version) });
        } else {
          summary.failed++;
          const code = r.error?.code ?? 'validation_failed';
          const message = r.error?.message ?? 'The server refused the change.';
          if (code === 'precondition_failed' || /does not match the current version/.test(message)) {
            await markConflict(workspace, ctx, draft, message);
          } else {
            workspace.markFailed(draft.key, { source: 'server', message, details: (r.error?.details ?? null) as never });
          }
        }
      }
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), '');
      for (const d of docs) {
        summary.failed++;
        workspace.markFailed(d.key, { source: err.status === 0 ? 'network' : 'server', message: err.message, details: err.details ?? null });
      }
    }
  }

  // 4. singleton defaults (section 6.3): one PATCH per template after the documents, so a new document's temp id was rewritten to its id
  for (const d of drafts.filter((x) => x.kind === 'template-selected' && !blocked.has(x.key))) {
    const selected = (d.current as { selected?: string | null } | null)?.selected ?? null;
    if (selected && isTempId(selected)) {
      summary.failed++;
      workspace.markFailed(d.key, { source: 'server', message: 'The chosen default is a new document that was not saved; save it first.' });
      continue;
    }
    if (dryRun) {
      summary.saved++;
      continue;
    }
    try {
      const result = await ctx.client.call(ops.updateTemplate, {
        path: { org: ctx.org, game: ctx.game, branch: ctx.branch, template: d.id },
        body: { selected, clearSelected: selected === null, message: ctx.message?.trim() || `Save All: the default document of ${d.id}` },
        ifMatch: d.base?.version ?? null,
      });
      if (result.commit) workspace.setHead(result.commit);
      summary.saved++;
      workspace.markSaved(d.key, { version: Number(result.data.version) });
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), '');
      summary.failed++;
      workspace.markFailed(d.key, { source: err.status === 0 ? 'network' : 'server', message: err.status === 412 ? 'The template changed on the server since; reload and choose again.' : err.message, details: err.details ?? null });
    }
  }

  // 5. the per-entity kinds (section 9.13): products, asset records, the localization settings, an environment, a platform record —
  //    one request each with If-Match of the base version; a 412 is a conflict, anything else a failure on the entity
  for (const d of drafts) {
    if (d.kind === 'document' || d.kind === 'string' || d.kind === 'template-selected' || blocked.has(d.key)) continue;
    // a player key needs the reason the shell asked for (section 11.4); refused here with the reason, never sent without one
    if (d.kind === 'player-key' && !dryRun && reasonOf(ctx).length < 2) {
      summary.failed++;
      workspace.markFailed(d.key, { source: 'server', message: 'A reason is required to save a player key: Save All asks for it.' });
      continue;
    }
    if (dryRun) {
      summary.saved++;
      continue;
    }
    try {
      const saved = await saveEntity(ctx, d);
      summary.saved++;
      if (saved.note) (summary.notes ??= []).push(saved.note);
      workspace.markSaved(d.key, saved);
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), '');
      summary.failed++;
      if (d.kind === 'player-key' && err.status === 409 && (err.details ?? []).some((x) => x.code === 'player_online')) {
        // the online guard (section 17.1): the draft stays, "Save anyway" sends force next time
        workspace.markConflict(d.key, { remoteVersion: null, remoteValue: undefined, message: err.message, online: true });
      } else if (err.status === 412) workspace.markConflict(d.key, { remoteVersion: null, remoteValue: undefined, message: 'Changed on the server since you loaded it; reload and re-apply your change.' });
      else workspace.markFailed(d.key, { source: err.status === 0 ? 'network' : 'server', message: err.message, details: err.details ?? null });
    }
  }

  if (!dryRun) workspace.afterSave();
  workspace.setLastSave(summary);
  return summary;
}

type Obj = Record<string, Json>;
const obj = (v: Json): Obj => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {});
const pick = (o: Obj, keys: string[]): Obj => Object.fromEntries(keys.filter((k) => k in o).map((k) => [k, o[k] ?? null]));

const PRODUCT_FIELDS = ['itemId', 'name', 'description', 'price', 'platform', 'type', 'baseId', 'iconUrl', 'localizedName', 'localizedDescription'];
const ASSET_FIELDS = ['name', 'kind', 'group', 'description', 'meta', 'flags'];
const ENVIRONMENT_FIELDS = ['name', 'isProtected', 'authLimit', 'presenceTimeoutSeconds', 'historyRetentionDays', 'historyMinVersionsPerKey', 'gameTimeOffsetSeconds', 'useLocalTime'];
const SETTINGS_FIELDS = ['defaultLanguage', 'includeKeyInDisplay', 'languages'];
const PLATFORM_FIELDS = ['titleId', 'appId', 's2sKey', 'enabled'];

/**
 * One request for a per-entity draft (section 9.13). Ids: a product's id (a temp id when new), an asset's id, `settings` for
 * the branch's localization settings, the environment's slug, `<env slug>/<platform>` for a platform record. The value of an
 * environment or platform draft carries the whole record; a platform's `s2sKey` is sent only when the draft set one. A
 * player key (`<env>/<player>/<collection>/<key>`, section 11.4) is PUT with the Save All reason and the draft's `force`.
 */
async function saveEntity(ctx: SaveContext, d: Draft): Promise<{ id?: string | null; version?: number | null; note?: string }> {
  const path = { org: ctx.org, game: ctx.game, branch: ctx.branch };
  const current = obj(d.current);
  const ifMatch = d.base?.version ?? null;
  const message = ctx.message?.trim() || undefined;
  switch (d.kind) {
    case 'product': {
      if (d.status === 'deleted') {
        await ctx.client.call(ops.deleteProduct, { path: { ...path, product: d.id }, ifMatch });
        return {};
      }
      if (isTempId(d.id)) {
        const r = await ctx.client.data(ops.createProduct, { path, body: { ...pick(current, PRODUCT_FIELDS), message: message ?? null } as never });
        return { id: r.id, version: Number(r.version) };
      }
      const r = await ctx.client.data(ops.updateProduct, { path: { ...path, product: d.id }, body: { ...pick(current, PRODUCT_FIELDS), message: message ?? null } as never, ifMatch });
      return { version: Number(r.version) };
    }
    case 'asset': {
      const r = await ctx.client.data(ops.updateAsset, { path: { ...path, asset: d.id }, body: { ...pick(current, ASSET_FIELDS), message: message ?? null } as never, ifMatch });
      return { version: Number(r.version) };
    }
    case 'localization-settings': {
      const r = await ctx.client.data(ops.updateLocalizationSettings, { path, body: { ...pick(current, SETTINGS_FIELDS), message: message ?? null } as never, ifMatch });
      return { version: Number(r.version) };
    }
    case 'environment': {
      const r = await ctx.client.data(ops.updateEnvironment, { path: { org: ctx.org, game: ctx.game, env: d.id }, body: pick(current, ENVIRONMENT_FIELDS) as never, ifMatch });
      return { version: Number(r.version) };
    }
    case 'platform-config': {
      const [env, platform] = d.id.split('/') as [string, string];
      const body = pick(current, PLATFORM_FIELDS);
      if (typeof body.s2sKey !== 'string' || body.s2sKey === '') delete body.s2sKey; // write-only: never echoed, sent only when set anew
      const r = await ctx.client.data(ops.putPlatformConfig, { path: { org: ctx.org, game: ctx.game, env, platform }, body: body as never, ifMatch: ifMatch === null ? undefined : ifMatch });
      return { version: Number(r.version) };
    }
    case 'player-key': {
      const [env, player, collection, ...rest] = d.id.split('/') as [string, string, string, ...string[]];
      const key = rest.join('/');
      const r = await ctx.client.data(ops.editPlayerSave, { path: { org: ctx.org, game: ctx.game, env, player, collection, key }, body: { value: d.current as never, reason: reasonOf(ctx), force: d.force === true } as never });
      const note = r.bump ? `${r.bump.collection}: unnySystem ${r.bump.from} → ${r.bump.to}` : undefined;
      return { version: Number(r.key.version), note };
    }
    default:
      throw new ApiError(0, 'unsupported', `Saving ${d.kind} drafts is not available.`, '');
  }
}

async function markConflict(workspace: Workspace, ctx: SaveContext, draft: Draft, message: string): Promise<void> {
  try {
    const current = await ctx.client.data(ops.getDocument, { path: { org: ctx.org, game: ctx.game, branch: ctx.branch, document: draft.id } });
    workspace.markConflict(draft.key, { remoteVersion: Number(current.version), remoteValue: current as unknown as Json, message, by: draft.remote?.by, at: draft.remote?.at });
  } catch {
    workspace.markConflict(draft.key, { remoteVersion: null, remoteValue: undefined, message });
  }
}
